import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/* =============================================================================
   Eduvora chart kit — hand-built SVG marks, zero dependencies.

   See styles/charts.css for why there is no charting library and for the palette
   validation numbers. This file is the mark layer.

   House rules baked in here so no caller has to remember them:
     · bars capped at 24px thick, 4px rounded data-end, square at the baseline
     · a 2px gap in the SURFACE colour separates touching marks (never a stroke)
     · lines 2px round-cap; dots r>=4 with a 2px surface ring
     · area fills are a 10% wash, never a saturated block
     · gridlines hairline + solid + recessive; never dashed
     · a legend whenever there are 2+ series; none for a single series
     · labels are selective — never a number on every point
     · TEXT NEVER WEARS THE SERIES COLOUR. Identity comes from the swatch beside it.
     · every chart carries a hover/focus tooltip AND a table view, so no value is
       reachable only by hovering
   ============================================================================= */

/* ---------------------------------------------------------------- formatting */

/** ₹ in Indian short scale. A trustee reads "₹2.7 Cr", never "₹27,138,000.00". */
export function rupeesShort(paise: number): string {
  const r = paise / 100;
  const neg = r < 0 ? '-' : '';
  const a = Math.abs(r);
  if (a >= 1e7) return `${neg}₹${(a / 1e7).toFixed(a >= 1e8 ? 0 : 1)} Cr`;
  if (a >= 1e5) return `${neg}₹${(a / 1e5).toFixed(a >= 1e6 ? 0 : 1)} L`;
  if (a >= 1e3) return `${neg}₹${Math.round(a / 1e3)}k`;
  return `${neg}₹${Math.round(a)}`;
}

/** Full precision, lakh-grouped — for tables and tooltips where the exact rupee matters. */
export function rupeesFull(paise: number): string {
  return '₹' + (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export function compactNum(n: number): string {
  if (Math.abs(n) >= 1e5) return (n / 1e5).toFixed(1) + 'L';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  return String(Math.round(n));
}

export const pct = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${n.toFixed(1)}%`;

/* ------------------------------------------------------------------- sizing */

/**
 * Measure the container so marks are laid out in real pixels.
 *
 * ⚠ Deliberately NOT a viewBox scale-to-fit. Scaling an SVG scales its text with it,
 * so a chart in a narrow column ends up with 7px axis labels. Measuring and laying
 * out in real px keeps type at a fixed readable size at every width.
 */
function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  return { ref, width };
}

/**
 * Clip a category label to whatever the gutter can actually hold.
 *
 * ⚠ This was a hardcoded 16 characters, which truncated "Class 10 — A · Physics" to
 * "Class 10 — A · …" in a 150px gutter with room to spare — the chart threw away the only part of
 * the label that distinguished one row from the next. Derive it from the width instead.
 */
function truncate(text: string, labelWidth: number): string {
  const max = Math.max(6, Math.floor((labelWidth - 14) / 6.6));
  return text.length > max ? text.slice(0, max - 1).trimEnd() + '…' : text;
}

/** Right-rounded bar. Square where it meets the baseline, 4px at the data end. */
function barPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w, h / 2));
  if (rr <= 0) return `M${x},${y}h${w}v${h}h${-w}z`;
  return `M${x},${y}h${w - rr}a${rr},${rr} 0 0 1 ${rr},${rr}v${h - 2 * rr}a${rr},${rr} 0 0 1 ${-rr},${rr}h${-(w - rr)}z`;
}

/** Axis ticks land on clean numbers — 0 / 25L / 50L, never 0 / 23.7L / 47.4L. */
function niceTicks(max: number, count = 4): number[] {
  if (!isFinite(max) || max <= 0) return [0];
  const raw = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(v);
  // ⚠ The loop can stop one step SHORT of the real maximum (max 27.1L, step 10L -> top tick 20L).
  // The series then paints above the plot area and over the legend. Always cover the data.
  if ((out[out.length - 1] ?? 0) < max) out.push((out[out.length - 1] ?? 0) + step);
  return out;
}

/* -------------------------------------------------------------------- types */

export interface Series {
  name: string;
  color: string;
  values: number[];
}

interface TipState {
  x: number;
  y: number;
  title: string;
  rows: { name: string; value: string; color: string; line?: boolean }[];
}

/* ------------------------------------------------------------------ tooltip */

function Tooltip({ tip, width }: { tip: TipState; width: number }) {
  // Flip before the tooltip runs off the right edge rather than after.
  const flip = tip.x > width - 150;
  return (
    <div
      className="viz-tip"
      style={{
        left: flip ? undefined : tip.x + 14,
        right: flip ? Math.max(0, width - tip.x + 14) : undefined,
        top: Math.max(0, tip.y - 12),
      }}
    >
      <div className="viz-tip-title">{tip.title}</div>
      {tip.rows.map((r, i) => (
        <div className="viz-tip-row" key={i}>
          <span className="viz-tip-name">
            <span
              className={r.line ? 'viz-key-line' : 'viz-key'}
              style={{ background: r.color }}
            />
            {r.name}
          </span>
          <span className="viz-tip-val">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------- frame */

export function Legend({ series, line = false }: { series: Series[]; line?: boolean }) {
  // A single series needs no legend — the title already names what is plotted, and a
  // one-swatch box just restates it.
  if (series.length < 2) return null;
  return (
    <div className="viz-legend">
      {series.map((s) => (
        <span className="viz-legend-item" key={s.name}>
          <span className={line ? 'viz-key-line' : 'viz-key'} style={{ background: s.color }} />
          {s.name}
        </span>
      ))}
    </div>
  );
}

export function ChartFrame({
  title,
  subtitle,
  series,
  legendAsLine,
  table,
  children,
}: {
  title: string;
  subtitle?: string;
  series?: Series[];
  legendAsLine?: boolean;
  table?: ReactNode;
  children: ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);
  return (
    <div className="viz viz-card">
      <div className="viz-head">
        <span className="viz-title">{title}</span>
        {series && series.length >= 2 && <Legend series={series} line={legendAsLine} />}
      </div>
      {subtitle && <p className="viz-sub">{subtitle}</p>}
      {children}
      {table && (
        <>
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="viz-table-toggle"
              aria-expanded={showTable}
              onClick={() => setShowTable((v) => !v)}
            >
              {showTable ? 'Hide table' : 'View as table'}
            </button>
          </div>
          {showTable && table}
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- stat tile */

export function StatTile({
  label,
  value,
  foot,
  trend,
  trendColor,
}: {
  label: string;
  value: string;
  foot?: ReactNode;
  trend?: number[];
  trendColor?: string;
}) {
  return (
    <div className="viz">
      <div className="viz-tile-label">{label}</div>
      <div className="viz-tile-value">{value}</div>
      {trend && trend.length > 1 && (
        <div style={{ marginTop: 6 }}>
          <Sparkline values={trend} color={trendColor ?? 'var(--s1)'} />
        </div>
      )}
      {foot && <div className="viz-tile-foot">{foot}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- sparkline */

export function Sparkline({
  values,
  color = 'var(--s1)',
  height = 26,
}: {
  values: number[];
  color?: string;
  height?: number;
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const w = width || 120;
  const pad = 3;

  const d = useMemo(() => {
    if (values.length < 2) return '';
    const max = Math.max(...values);
    const min = Math.min(...values);
    const span = max - min || 1;
    const stepX = (w - pad * 2) / (values.length - 1);
    return values
      .map((v, i) => {
        const x = pad + i * stepX;
        const y = pad + (1 - (v - min) / span) * (height - pad * 2);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }, [values, w, height]);

  const last = values.length ? values[values.length - 1] : 0;
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const lastY = pad + (1 - (last - min) / span) * (height - pad * 2);

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={w} height={height} role="presentation">
        <path className="viz-line" d={d} stroke={color} />
        {values.length > 1 && (
          <circle className="viz-dot" cx={w - pad} cy={lastY} r={3} fill={color} />
        )}
      </svg>
    </div>
  );
}

/* -------------------------------------------------------------------- meter */

/**
 * A single proportion. The unfilled track is a lighter step of the same ramp so the
 * state reads across the whole bar, not only where it is filled.
 */
export function Meter({
  value,
  color = 'var(--s1)',
  height = 8,
  label,
}: {
  value: number; // 0..100
  color?: string;
  height?: number;
  label?: string;
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const w = width || 160;
  const clamped = Math.max(0, Math.min(100, isFinite(value) ? value : 0));
  const fill = (clamped / 100) * w;

  return (
    <div ref={ref} className="viz" style={{ width: '100%' }}>
      <svg width={w} height={height} role="img" aria-label={label ?? `${clamped.toFixed(1)} percent`}>
        {/* ⚠ The track derives from the fill, so a warning-coloured meter does not sit in a blue
            trough. Set inline because a CSS `fill` rule would beat the presentation attribute. */}
        <rect
          x={0}
          y={0}
          width={w}
          height={height}
          rx={height / 2}
          style={{ fill: `color-mix(in srgb, ${color} 22%, transparent)` }}
        />
        {fill > 0 && <path d={barPath(0, 0, Math.max(fill, height / 2), height, height / 2)} fill={color} />}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------- horizontal bar chart */

export interface HBarProps {
  categories: string[];
  series: Series[]; // 1 series = plain bars, 2+ = stacked
  format?: (n: number) => string;
  labelWidth?: number;
  valueWidth?: number;
  barThickness?: number;
  onSelect?: (index: number) => void;
  activeIndex?: number | null;
  /** Rendered inside the tooltip under the series rows — e.g. the class's toppers. */
  extraTip?: (index: number) => { name: string; value: string; color: string }[];
  /**
   * Per-bar colour override, for a single-series chart whose bars carry a STATE rather than an
   * identity (attendance bands: healthy / watch / serious).
   *
   * ⚠ Only legal because every bar is directly labelled with its value — a status colour must
   * never be the only thing carrying the meaning.
   */
  colorAt?: (index: number) => string;
}

export function HBarChart({
  categories,
  series,
  format = compactNum,
  labelWidth = 92,
  valueWidth = 62,
  barThickness = 18,
  onSelect,
  activeIndex,
  extraTip,
  colorAt,
}: HBarProps) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [tip, setTip] = useState<TipState | null>(null);

  const w = width || 480;
  const rowH = Math.max(barThickness + 12, 26); // >= 24px hit target
  const plotX = labelWidth;
  const plotW = Math.max(40, w - labelWidth - valueWidth);
  const height = categories.length * rowH;

  const totals = categories.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const max = Math.max(...totals, 1);

  const GAP = 2; // the surface gap — white doing the separating, never a stroke

  return (
    <div ref={ref} className="viz" style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} role="img" aria-label="Bar chart">
        {categories.map((cat, i) => {
          const y = i * rowH;
          const barY = y + (rowH - barThickness) / 2;
          const total = totals[i];
          let cursor = plotX;

          const segs = series.map((s, si) => {
            const v = s.values[i] ?? 0;
            const raw = (v / max) * plotW;
            const isLast = si === series.length - 1 || series.slice(si + 1).every((n) => (n.values[i] ?? 0) <= 0);
            const segW = Math.max(0, raw - (si > 0 ? GAP : 0));
            const x = cursor + (si > 0 ? GAP : 0);
            cursor = x + segW;
            return { x, w: segW, color: colorAt ? colorAt(i) : s.color, isLast, v };
          });

          const active = activeIndex === i;

          return (
            <g
              key={cat + i}
              className="viz-row"
              tabIndex={onSelect ? 0 : -1}
              role={onSelect ? 'button' : undefined}
              onFocus={(e) => {
                const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                setTip({
                  x: plotX + 40,
                  y: y + rowH,
                  title: cat,
                  rows: [
                    ...series.map((s) => ({ name: s.name, value: format(s.values[i] ?? 0), color: s.color })),
                    ...(extraTip ? extraTip(i) : []),
                  ],
                });
                void box;
              }}
              onBlur={() => setTip(null)}
              onPointerMove={(e) => {
                const box = e.currentTarget.ownerSVGElement!.getBoundingClientRect();
                setTip({
                  x: e.clientX - box.left,
                  y: e.clientY - box.top,
                  title: cat,
                  rows: [
                    ...series.map((s) => ({ name: s.name, value: format(s.values[i] ?? 0), color: s.color })),
                    ...(extraTip ? extraTip(i) : []),
                  ],
                });
              }}
              onPointerLeave={() => setTip(null)}
              onClick={() => onSelect?.(i)}
              onKeyDown={(e) => {
                if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSelect(i);
                }
              }}
            >
              {/* hit target spans the whole row, not just the painted pixels */}
              <rect className="viz-hit" x={0} y={y} width={w} height={rowH} />

              <text
                className="viz-cat"
                x={labelWidth - 10}
                y={y + rowH / 2}
                textAnchor="end"
                dominantBaseline="central"
                style={active ? { fill: 'var(--viz-ink)', fontWeight: 600 } : undefined}
              >
                {truncate(cat, labelWidth)}
              </text>

              {segs.map((sg, si) =>
                sg.w > 0 ? (
                  <path
                    key={si}
                    className="viz-mark"
                    d={
                      sg.isLast
                        ? barPath(sg.x, barY, sg.w, barThickness, 4)
                        : `M${sg.x},${barY}h${sg.w}v${barThickness}h${-sg.w}z`
                    }
                    fill={sg.color}
                  />
                ) : null,
              )}

              {/* value at the tip — in the right gutter, so it can never be clipped by its own bar */}
              <text
                className="viz-value"
                x={w - valueWidth + 8}
                y={y + rowH / 2}
                dominantBaseline="central"
              >
                {format(total)}
              </text>

              {active && (
                <rect
                  className="viz-focus"
                  x={0.5}
                  y={y + 0.5}
                  width={w - 1}
                  height={rowH - 1}
                  rx={4}
                  fill="none"
                  stroke="var(--viz-axis)"
                />
              )}
            </g>
          );
        })}
      </svg>
      {tip && <Tooltip tip={tip} width={w} />}
    </div>
  );
}

/* ------------------------------------------------------------- line / area */

export function LineChart({
  labels,
  series,
  format = compactNum,
  height = 190,
  area = true,
}: {
  labels: string[];
  series: Series[];
  format?: (n: number) => string;
  height?: number;
  area?: boolean;
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const w = width || 520;
  const padL = 46;
  const padR = 12;
  const padT = 8;
  const padB = 24;
  const plotW = Math.max(20, w - padL - padR);
  const plotH = Math.max(20, height - padT - padB);

  const allValues = series.flatMap((s) => s.values);
  const rawMax = Math.max(...allValues, 0);
  const ticks = niceTicks(rawMax);
  const max = Math.max(ticks[ticks.length - 1] ?? 1, 1);

  const xAt = (i: number) => padL + (labels.length <= 1 ? plotW / 2 : (i / (labels.length - 1)) * plotW);
  const yAt = (v: number) => padT + (1 - v / max) * plotH;

  // Show every label only when they fit; otherwise thin them out rather than let them collide.
  const labelStep = Math.max(1, Math.ceil(labels.length / Math.max(2, Math.floor(plotW / 46))));

  return (
    <div ref={ref} className="viz" style={{ position: 'relative', width: '100%' }}>
      <svg
        width={w}
        height={height}
        role="img"
        aria-label="Trend chart"
        onPointerMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - box.left;
          if (labels.length < 2) return setHover(0);
          const i = Math.round(((x - padL) / plotW) * (labels.length - 1));
          setHover(Math.max(0, Math.min(labels.length - 1, i)));
        }}
        onPointerLeave={() => setHover(null)}
      >
        {ticks.map((t, i) => (
          <g key={i}>
            <line className="viz-gridline" x1={padL} x2={padL + plotW} y1={yAt(t)} y2={yAt(t)} />
            <text className="viz-tick" x={padL - 8} y={yAt(t)} textAnchor="end" dominantBaseline="central">
              {format(t)}
            </text>
          </g>
        ))}

        <line className="viz-baseline" x1={padL} x2={padL + plotW} y1={yAt(0)} y2={yAt(0)} />

        {area &&
          series.map((s) => (
            <path
              key={'a' + s.name}
              className="viz-area"
              fill={s.color}
              d={
                `M${xAt(0)},${yAt(0)} ` +
                s.values.map((v, i) => `L${xAt(i)},${yAt(v)}`).join(' ') +
                ` L${xAt(s.values.length - 1)},${yAt(0)} Z`
              }
            />
          ))}

        {series.map((s) => (
          <path
            key={'l' + s.name}
            className="viz-line"
            stroke={s.color}
            d={s.values.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i)},${yAt(v)}`).join(' ')}
          />
        ))}

        {hover !== null && (
          <>
            <line
              className="viz-crosshair"
              x1={xAt(hover)}
              x2={xAt(hover)}
              y1={padT}
              y2={padT + plotH}
            />
            {series.map((s) => (
              <circle
                key={'d' + s.name}
                className="viz-dot"
                cx={xAt(hover)}
                cy={yAt(s.values[hover] ?? 0)}
                r={4}
                fill={s.color}
              />
            ))}
          </>
        )}

        {labels.map((l, i) =>
          i % labelStep === 0 ? (
            <text key={l + i} className="viz-tick" x={xAt(i)} y={height - 6} textAnchor="middle">
              {l}
            </text>
          ) : null,
        )}
      </svg>

      {hover !== null && (
        <Tooltip
          tip={{
            x: xAt(hover),
            y: padT + 6,
            title: labels[hover] ?? '',
            rows: series.map((s) => ({
              name: s.name,
              value: format(s.values[hover] ?? 0),
              color: s.color,
              line: true,
            })),
          }}
          width={w}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- donut */

export function Donut({
  slices,
  format = compactNum,
  size = 148,
  centerLabel,
  centerValue,
}: {
  slices: { name: string; value: number; color: string }[];
  format?: (n: number) => string;
  size?: number;
  centerLabel?: string;
  centerValue?: string;
}) {
  const [tip, setTip] = useState<TipState | null>(null);
  const total = slices.reduce((s, x) => s + Math.max(0, x.value), 0);
  const r = size / 2;
  const thickness = Math.max(14, size * 0.17);
  const ir = r - thickness;

  // 2px of surface between neighbouring arcs, expressed as an angle.
  const gapAngle = total > 0 ? Math.min(0.05, 2 / r) : 0;

  let angle = -Math.PI / 2;
  const arcs = slices.map((s) => {
    const frac = total > 0 ? Math.max(0, s.value) / total : 0;
    const sweep = frac * Math.PI * 2;
    const a0 = angle + gapAngle / 2;
    const a1 = angle + sweep - gapAngle / 2;
    angle += sweep;

    if (a1 <= a0) return { ...s, d: '', frac };

    const large = a1 - a0 > Math.PI ? 1 : 0;
    const d =
      `M${r + Math.cos(a0) * r},${r + Math.sin(a0) * r}` +
      `A${r},${r} 0 ${large} 1 ${r + Math.cos(a1) * r},${r + Math.sin(a1) * r}` +
      `L${r + Math.cos(a1) * ir},${r + Math.sin(a1) * ir}` +
      `A${ir},${ir} 0 ${large} 0 ${r + Math.cos(a0) * ir},${r + Math.sin(a0) * ir}Z`;
    return { ...s, d, frac };
  });

  return (
    <div className="viz" style={{ position: 'relative', width: size }}>
      <svg width={size} height={size} role="img" aria-label="Share chart">
        {arcs.map((a) =>
          a.d ? (
            <path
              key={a.name}
              className="viz-mark"
              d={a.d}
              fill={a.color}
              onPointerMove={(e) => {
                const box = e.currentTarget.ownerSVGElement!.getBoundingClientRect();
                setTip({
                  x: e.clientX - box.left,
                  y: e.clientY - box.top,
                  title: a.name,
                  rows: [{ name: `${(a.frac * 100).toFixed(1)}%`, value: format(a.value), color: a.color }],
                });
              }}
              onPointerLeave={() => setTip(null)}
            />
          ) : null,
        )}
        {centerValue && (
          <text
            x={r}
            y={r - 4}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: 18, fontWeight: 650, fill: 'var(--viz-ink)' }}
          >
            {centerValue}
          </text>
        )}
        {centerLabel && (
          <text
            x={r}
            y={r + 14}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: 10.5, fill: 'var(--viz-muted)' }}
          >
            {centerLabel}
          </text>
        )}
      </svg>
      {tip && <Tooltip tip={tip} width={size} />}
    </div>
  );
}
